import { Helmet } from 'react-helmet-async';
import ContactUsComponent from '../../components/ContactUsComponent'

const deliverables = [
  "Completely rebuilt and re-engineered the church directory platform",
  "Empowering the system with AI and NLP to upgrade the filtering system",
  "Automating the categorization of church members and groups with the help of machine learning",
  "Speeding up the data reception from various sources",
  "Automating the process of alerting members about events, meetings, and updates",
  "Developing a smart search engine with faceted search, entity recognition, and Natural Language Processing",
  "Implementing the full Rest API to integrate the system with other church management tools",
  "Enabling the product with the ability to collect data from various sources for comprehensive church management",
  "Setting up the software development and business processes"
];

const ChurchDirectory = () => {
  return (
    <>
      <Helmet>
        <title>My Church Directory - Church Management Software by Data Repo</title>
        <meta
          name="description"
          content="My Church Directory is an AI-powered church management software developed by Data Repo. Manage members, families, groups, events, and communication with an intelligent, scalable platform built to strengthen church communities."
        />
      </Helmet>

      <section
        style={{
          backgroundImage: `url(../images/bg-casestudies-box.png)`,
          backgroundSize: "100% 80vh"
        }}
        className="hero--container relative flex flex-col justify-center w-full h-[60vh] bg-no-repeat bg-fixed md:bg-origin-border md:h-[80vh] pt-[65px]">
        <h1 className="text-center text-white text-2xl md:text-5xl font-bold uppercase">My Church Directory</h1>
        <div className="row--image-style bg-transparent w-full absolute bottom-0 left-0 right-0">
          <svg className="waves w-full h-44" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1000 300" preserveAspectRatio="none">
            <path fill="rgb(226 232 240)" d="M 1000 280 l 2 -253 c -155 -36 -310 135 -415 164 c -102.64 28.35 -149 -32 -235 -31 c -80 1 -142 53 -229 80 c -65.54 20.34 -101 15 -126 11.61 v 54.39 z"></path>
            <path fill="rgb(100 116 139)" d="M 1000 261 l 2 -222 c -157 -43 -312 144 -405 178 c -101.11 33.38 -159 -47 -242 -46 c -80 1 -153.09 54.07 -229 87 c -65.21 25.59 -104.07 16.72 -126 16.61 v 22.39 z"></path>
            <path fill="#E5E7EB" d="M 1000 300 l 1 -230.29 c -217 -12.71 -300.47 129.15 -404 156.29 c -103 27 -174 -30 -257 -29 c -80 1 -130.09 37.07 -214 70 c -61.23 24 -108 15.61 -126 10.61 v 22.39 z"></path>
          </svg>
        </div>
      </section>

      <section className="w-full pt-10 px-8 bg-gradient-to-r from-blue-100 via-white to-blue-100 relative overflow-hidden">
        <div className="absolute -top-20 -left-20 w-72 h-72 bg-blue-200 rounded-full opacity-30 filter blur-3xl"></div>
        <div className="absolute -bottom-20 -right-20 w-96 h-96 bg-purple-200 rounded-full opacity-20 filter blur-3xl"></div>

        <div className="flex flex-col md:flex-row items-center justify-center px-16 relative z-10">
          <div className="md:w-1/2 flex justify-center items-center p-5">
            <div className="bg-white rounded-xl shadow-2xl p-4 hover:scale-105 transform transition-transform duration-300">
              <img
                src="../images/mycdir-new.png"
                alt="car rental software dashboard"
                className="rounded-lg"
              />
            </div>
          </div>
          <div className="md:w-1/2 flex justify-center items-center p-5">
            <div className="bg-white rounded-xl shadow-2xl p-4 hover:scale-105 transform transition-transform duration-300">
              <img
                src="../images/mycdir-members.png"
                alt="car rental software dashboard"
                className="rounded-lg"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="w-full items-center justify-center bg-gray-200">
        <div className="max-w-screen-lg mx-auto py-8">
          <div className="px-8 md:px-1 md:pr-2 py-6">
            <div className="mb-4 text-lg font-normal text-center md:text-left text-gray-600">
              <p>
                <strong>Industry:</strong> Church Management; Administration; Community Management
              </p>
              <p>
                <strong>Location:</strong> USA
              </p>
              <p>
                <strong>Areas of expertise:</strong> Back-end; Front-end; Quality assurance; Project management; AI
              </p>
              <p>
                <strong>Timeline:</strong> 2017-now
              </p>
            </div>

            <p className="text-xl font-normal text-center md:text-left text-gray-600">
              My Church Directory is a comprehensive tool developed by Data Repo for church organizations. It allows churches to manage their administration, members, families, groups, and events efficiently.
            </p>

            <div className="pt-8">
              <h2 className="text-black text-2xl md:text-3xl font-bold py-5">
                Product Rebuilding and AI Integration
              </h2>
              <p className="text-xl font-normal text-center md:text-left text-gray-600">
                My Church Directory aggregates data from emails, websites, and social media to provide a single platform for managing church operations. This solution reduces administrative workload and enhances community engagement.
                <br /><br />
                Working closely with church leaders, the Data Repo team re-engineered the product, delivering a robust backend and intuitive frontend. We also integrated AI and machine learning to improve member management and event planning.
              </p>
            </div>

            <div className="pt-8">
              <h2 className="text-black text-2xl md:text-3xl font-bold py-5">
                Key Features
              </h2>
              <p className="text-xl font-normal text-center md:text-left text-gray-600 pb-8">
                My Church Directory isn’t just a database — it’s the heartbeat of a connected church community.
                Purpose-built to empower pastors, leaders, and members alike, it transforms the way churches manage people, events, and ministries.
                <br /><br />
                Explore below how each feature works together to create a powerful, unified, and inspiring church management experience that brings your congregation closer than ever.
              </p>

              <div className="space-y-8 text-xl font-normal text-gray-600">
                <div>
                  <h3 className="text-2xl font-semibold text-black pb-2">
                    1. Comprehensive Group and Subgroup Management
                  </h3>
                  <p>
                    Structure your ministry exactly how your church operates in real life. My Church Directory allows you to build a clear hierarchy of groups and subgroups —
                    from main congregations to ministries, outreach teams, and departments — each with their own members, activities, and leadership roles.
                    <br /><br />
                    This organization gives every ministry the autonomy to thrive while keeping all branches connected under one centralized system —
                    ensuring unity, clarity, and collaboration across the entire church body.
                  </p>
                </div>

                <div>
                  <h3 className="text-2xl font-semibold text-black pb-2">
                    2. Advanced User Management and Role Control
                  </h3>
                  <p>
                    Simplify church administration with powerful role-based access and user management.
                    My Church Directory lets you create detailed profiles for pastors, leaders, volunteers, and members — assigning permissions that match their roles and responsibilities.
                    <br /><br />
                    Leadership teams can monitor attendance, engagement, and communication in one place — empowering pastors to focus on people, not paperwork.
                    With role-based control, every user sees only what they need, ensuring both security and efficiency across the organization.
                  </p>
                </div>

                <div>
                  <h3 className="text-2xl font-semibold text-black pb-2">
                    3. Family and Member Relationship Tracking
                  </h3>
                  <p>
                    Ministry starts with relationships — and My Church Directory helps you nurture them.
                    Link family members, track generations, and understand how households engage with the church through connected family profiles.
                    <br /><br />
                    This feature enables churches to personalize outreach, strengthen family ministries, and ensure that no member feels overlooked.
                    Whether it’s following up with parents or encouraging youth involvement, the system helps you serve every family with care and precision.
                  </p>
                </div>

                <div>
                  <h3 className="text-2xl font-semibold text-black pb-2">
                    4. Intelligent Event and Activity Management
                  </h3>
                  <p>
                    Keep your church organized and connected with structured event scheduling.
                    My Church Directory allows administrators to easily create and manage events for specific groups and subgroups — from Sunday services to community gatherings — all within one central platform.
                    <br /><br />
                    Simply set dates, assign groups, and manage participation seamlessly.
                    It’s a simple yet powerful way to coordinate your church’s activities and ensure everyone stays on the same page.
                  </p>
                </div>

                <div>
                  <h3 className="text-2xl font-semibold text-black pb-2">
                    5. User Access & Support Management
                  </h3>
                  <p>
                    My Church Directory gives administrators complete visibility and control over group access and member activity.
                    You can easily view login devices for specific groups, helping ensure account security and proper usage across your organization.
                    <br /><br />
                    The platform also includes a built-in support ticket system, allowing users to raise and track issues or requests directly within the system.
                    This ensures faster resolutions and smoother communication between members and administrators.
                  </p>
                </div>

                <div>
                  <h3 className="text-2xl font-semibold text-black pb-2">
                    6. Centralized Management Dashboard
                  </h3>
                  <p>
                    My Church Directory brings everything under one roof with a unified dashboard that simplifies daily administration.
                    From one place, administrators can manage users, groups, subgroups, families, family members, and events with complete ease and visibility.
                    <br /><br />
                    Whether it’s adding new members, organizing families, or creating group events, the dashboard keeps all church information structured and accessible — ensuring smooth coordination and clear oversight for every part of your ministry.
                  </p>
                </div>
              </div>

              <p className="text-xl font-normal text-center md:text-left text-gray-600 pt-10">
                Together, these features make My Church Directory more than a management tool — it’s the foundation of a thriving, connected, and mission-driven church.
                From streamlining daily operations to deepening relationships, it helps your ministry grow stronger, smarter, and more united every day.
              </p>
            </div>


            <div className="pt-8">
              <h2 className="text-black text-2xl md:text-3xl font-bold py-5">
                Deliverables
              </h2>
              <ul className="list-disc pl-5 text-xl font-normal text-center md:text-left text-gray-600">
                {deliverables.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </div>

            <div className="pt-8">
              <h2 className="text-black text-2xl md:text-3xl font-bold py-5">
                Project Results
              </h2>
              <p className="text-xl font-normal text-center md:text-left text-gray-600 pb-8">
                Data Repo's My Church Directory has transformed church management by streamlining administrative tasks and improving communication. The system's advanced features and scalability support ongoing growth and community engagement.
              </p>
              <ul className="list-disc pl-5 text-xl font-normal text-center md:text-left text-gray-600">
                <li>Increased efficiency in managing church operations and member data.</li>
                <li>Enhanced community engagement through better event management and communication tools.</li>
                <li>Significant time and cost savings for church administrations.</li>
              </ul>
            </div>
          </div>
        </div>
      </section>
      <ContactUsComponent />
    </>
  )
}

export default ChurchDirectory;